import { CANVAS_GATEWAY_BASE_URL, CANVAS_GATEWAY_CONFIGURED } from "@/flags";
import { TOKEN_KEY } from "@/constants/auth";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { isNotepadSurface } from "@/features/notepad/utils/teachingSurface";
import { useNotepadStore } from "@/features/notepad/store/useNotepadStore";
import {
  notepadCanvasStateKey,
  notepadCanvasStatePrefix,
} from "@/features/tutor/vo-demo/utils/notepadPersistence";

/**
 * Canvas session summary is now sent over the canvas WebSocket as a
 * CANVAS_SESSION_MESSAGES chunk during canvas close / page unload.
 * This HTTP fallback path has been removed — gateway is always the target.
 *
 * Kept as a module boundary in case new canvas HTTP endpoints are needed.
 */

export const gatewayUrl = (path: string) =>
  `${CANVAS_GATEWAY_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;

const PERSIST_CHECKPOINT_TIMEOUT_MS = 2000;

/* Flush gateway history and the board with its checkpoint tokens before navigation.
 * Warn and continue after failures; accepted saves advance the checkpoint revision. */
export async function persistNotepadCheckpoint(): Promise<void> {
  if (!CANVAS_GATEWAY_CONFIGURED) return;

  const slideState = useSlideStore.getState();
  const currentSlideIndex: number = slideState.currentSlideIndex;
  // slides is unordered — resolve by slide_index, not by array position.
  const currentSlide = slideState.getSlideByIndex(currentSlideIndex);
  // teaching_surface is the canonical marker; the legacy notepad_bite metadata
  // key is retired server-side whenever teaching_surface is written.
  if (!isNotepadSurface(currentSlide?.metadata)) return;

  const sessionId = useSessionStore.getState().sessionId;
  const lessonId = slideState.currentLessonId;
  if (!sessionId || lessonId == null) return;

  const snapshot = _readNotepadTldrawSnapshot(sessionId, currentSlideIndex);
  // Capture the originating activity with the snapshot, before any await.
  const checkpointToken = useNotepadStore.getState().checkpointToken;
  const canSaveSnapshot =
    snapshot &&
    checkpointToken?.sessionId === sessionId &&
    checkpointToken.slideIndex === currentSlideIndex;

  const token = localStorage.getItem(TOKEN_KEY);
  try {
    const response = await fetch(gatewayUrl("/notepad/persist"), {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        session_id: sessionId,
        lesson_id: Number(lessonId),
        slide_index: currentSlideIndex,
        ...(canSaveSnapshot
          ? {
              snapshot,
              state_version: checkpointToken.stateVersion,
              snapshot_revision: checkpointToken.snapshotRevision,
            }
          : {}),
      }),
      signal: AbortSignal.timeout(PERSIST_CHECKPOINT_TIMEOUT_MS),
    });
    if (!response.ok) {
      console.warn(
        "persistNotepadCheckpoint non-2xx:",
        response.status,
        "— per-turn Redis writes remain the backstop",
      );
    } else if (canSaveSnapshot) {
      const result = await response.json();
      if (result.snapshot_saved === true) {
        useNotepadStore
          .getState()
          .advanceCheckpointRevision(checkpointToken, result.snapshot_revision);
      }
    }
  } catch (err) {
    console.warn(
      "persistNotepadCheckpoint failed — per-turn Redis writes remain the backstop:",
      err,
    );
  }
}

/*
 * Input:  sessionId + slide index (derive the sessionStorage page key)
 * Output: the tldraw editor snapshot saved by TldrawCanvasPanel (kept fresh by
 *         its per-turn persist and flushCanvasState), or null
 *
 * The key format itself belongs to notepadPersistence — the same module that
 * clears these keys — so this reader only decides WHICH key to ask for: the
 * current page in unlimited mode, the bare slide key otherwise. The prefix scan
 * is the last resort in case the mode flag changed between the panel's write
 * and this read.
 */
function _readNotepadTldrawSnapshot(
  sessionId: string,
  currentSlideIndex: number,
): unknown | null {
  try {
    const { currentPageIndex, isLimitedMode } = useNotepadStore.getState();
    const pagedKey = notepadCanvasStateKey(
      sessionId,
      currentSlideIndex,
      isLimitedMode ? null : currentPageIndex,
    );
    const slidePrefix = notepadCanvasStatePrefix(sessionId, currentSlideIndex);

    const candidateKeys = [pagedKey, slidePrefix];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const storageKey = sessionStorage.key(i);
      if (
        storageKey &&
        storageKey.startsWith(`${slidePrefix}-page-`) &&
        !candidateKeys.includes(storageKey)
      ) {
        candidateKeys.push(storageKey);
      }
    }

    for (const candidateKey of candidateKeys) {
      const raw = sessionStorage.getItem(candidateKey);
      if (!raw) continue;
      const saved = JSON.parse(raw) as { snapshot?: unknown };
      if (saved.snapshot) return saved.snapshot;
    }
    return null;
  } catch {
    return null;
  }
}
